import React, { useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTotalSupply, useBurnedBalance } from 'hooks/useTokenBalance';
import { getCakeAddress, getHVIAddress } from 'utils/addressHelpers';
import { getBalanceNumber } from 'utils/formatBalance';
import { usePriceCakeBusd, usePriceHVIBusd } from 'state/farms/hooks';
import { Flex, Text, Box, Skeleton, Image, Button } from '@pancakeswap/uikit';
import { useTranslation } from 'contexts/Localization';
import Balance from 'components/Balance';

const glow = keyframes`
  0% {
    box-shadow: 0 0 5px #53b28c, 0 0 10px #53b28c, 0 0 15px #53b28c, 0 0 20px #53b28c;
  }
  100% {
    box-shadow: 0 0 10px #53b28c, 0 0 15px #53b28c, 0 0 20px #53b28c, 0 0 25px #53b28c;
  }
`;

const StyledBox = styled(Box)`
  border: 1px solid #333;
  border-radius: 8px;
  background-color: #000;
  padding: 16px;
  width: 100%;
  max-width: 550px;
  text-align: center;
  display: flex;
  flex-direction: column;
  animation: ${glow} 2s infinite alternate;
  box-shadow: 0 0 5px #53b28c, 0 0 10px #53b28c, 0 0 15px #53b28c, 0 0 20px #53b28c;
  align-items: center;
`;

const ImageWrapper = styled.div`
  margin-right: 4px;
  width: 30px;
  height: 30px;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-right: 4px;
    width: 30px;
    height: 30px;
  }
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  margin-top: 8px;

  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
    flex-wrap: nowrap;
  }
`;

const DataRow = ({ label, value, prefix }) => (
  <Flex alignItems="center" justifyContent="space-between" width="100%" my="4px">
    <Text color="textSubtle" fontSize="15px" mr="8px">{label}</Text>
    {value !== null ? (
      <Balance decimals={0} fontSize="15px" value={value} prefix={prefix} />
    ) : (
      <Skeleton height={18} width={126} />
    )}
  </Flex>
);

const CakeDataRow = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Lekérdezések a hook-okból
  const totalSupply = useTotalSupply();
  const burnedBalance = getBalanceNumber(useBurnedBalance(getCakeAddress()));
  const cakePriceBusd = usePriceCakeBusd();
  const totalSupplyHVI = useTotalSupply();
  const burnedBalanceHVI = getBalanceNumber(useBurnedBalance(getHVIAddress()));
  const cakePriceBusdHVI = usePriceHVIBusd();

  const fetchData = useCallback(() => {
    try {
      setLoading(true);
      setError(null);

      // Adatok számítása
      const cakeSupply = totalSupply ? getBalanceNumber(totalSupply) - burnedBalance : 0;
      const cakeSupply2 = totalSupply ? getBalanceNumber(totalSupply) : 0;
      const mcap = cakePriceBusd.times(cakeSupply2).toNumber();
      const vizslaprice = cakePriceBusd.toNumber();

      const cakeSupplyHVI = totalSupplyHVI ? getBalanceNumber(totalSupplyHVI) - burnedBalanceHVI : 0;
      const cakeSupplyHVI2 = totalSupplyHVI ? getBalanceNumber(totalSupplyHVI) : 0;
      const mcapHVI = cakePriceBusdHVI.times(cakeSupplyHVI2).times(1000000000).toNumber();
      const hvprice = cakePriceBusdHVI.toNumber();

      setData({
        vizslaprice,
        cakeSupply2,
        cakeSupply,
        burnedBalance,
        mcap,
        cakeSupplyHVI: cakeSupplyHVI * 1000000000,
        cakeSupplyHVI2: cakeSupplyHVI2 * 1000000000,
        burnedBalanceHVI: burnedBalanceHVI * 1000000000,
        mcapHVI,
        hvprice
      });
    } catch (err) {
      console.error(err);
      setError('Hiba történt az adatok betöltésekor.');
    } finally {
      setLoading(false);
    }
  }, [totalSupply, burnedBalance, cakePriceBusd, totalSupplyHVI, burnedBalanceHVI, cakePriceBusdHVI]);

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Hook változók figyelése

  if (loading) return <Skeleton height={50} />;
  if (error) return (
    <Box>
      <Text color="red">{error}</Text>
      <Button onClick={fetchData}>Próbáld újra</Button>
    </Box>
  );

  return (
    <StyledBox>
      <Grid>
        <Box>
          <Flex alignItems="center" justifyContent="space-between" width="100%" my="4px">
            <Flex alignItems="center">
              <ImageWrapper>
                <Image
                  src="/images/tokens/0xadaae082237cb1772c9e079db95c117e6dd0c5af.png"
                  width={30}
                  height={30}
                />
              </ImageWrapper>
              <Text fontSize="15px" color="textSubtle">{t('VIZSLASWAP')}</Text>
            </Flex>
            {data.vizslaprice ? (
              <Balance decimals={4} fontSize="15px" value={data.vizslaprice} prefix="$" />
            ) : (
              <Skeleton height={18} width={126} />
            )}
          </Flex>
          <DataRow label={t('Total supply')} value={data.cakeSupply2} prefix="" />
          <DataRow label={t('Circulation supply')} value={data.cakeSupply} prefix="" />
          <DataRow label={t('Burned')} value={data.burnedBalance} prefix="" />
          <DataRow label={t('Market cap')} value={data.mcap} prefix="$" />
        </Box>

        <Box>
          <Flex alignItems="center" justifyContent="space-between" width="100%" my="4px">
            <Flex alignItems="center">
              <ImageWrapper>
                <Image
                  src="/images/pixel/P1.png"
                  width={30}
                  height={30}
                />
              </ImageWrapper>
              <Text fontSize="15px" color="textSubtle">{t('HVI')}</Text>
            </Flex>
            {data.hvprice ? (
              <Balance decimals={15} mr="4px" fontSize="15px" value={data.hvprice} prefix="$" />
            ) : (
              <Skeleton height={18} width={126} />
            )}
          </Flex>
          <DataRow label={t('Total supply')} value={data.cakeSupplyHVI} prefix="" />
          <DataRow label={t('Circulation supply')} value={data.cakeSupplyHVI2} prefix="" />
          <DataRow label={t('Burned')} value={data.burnedBalanceHVI} prefix="" />
          <DataRow label={t('Market cap')} value={data.mcapHVI} prefix="$" />
        </Box>
      </Grid>
    </StyledBox>
  );
};

export default CakeDataRow;
